import { Navbar, Nav, Container, Button, Row, Col, Card, Form, Alert, Carousel } from 'react-bootstrap';
import { useState, useEffect, useRef } from 'react';
import Footer from './components/Footer';

function Home() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    _honey: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [openCard, setOpenCard] = useState(null);
  const [navExpanded, setNavExpanded] = useState(false);
  const [missionVisible, setMissionVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [whyVisible, setWhyVisible] = useState(false);
  const [customersVisible, setCustomersVisible] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);
  const [statCounters, setStatCounters] = useState([0, 0]);
  const missionRef = useRef(null);
  const everythingRef = useRef(null);
  const whyRef = useRef(null);
  const customersRef = useRef(null);
  const contactRef = useRef(null);

  const toggleCard = (index) => {
    setOpenCard(openCard === index ? null : index);
  };

  useEffect(() => {
    const handleClick = (e) => {
      const href = e.target.closest('a')?.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          setTimeout(() => {
            const navbar = document.querySelector('.navbar-custom');
            const navbarHeight = navbar ? navbar.offsetHeight : 0;
            const top = element.getBoundingClientRect().top + window.pageYOffset - navbarHeight - 16;
            window.scrollTo({ top, behavior: 'smooth' });
          }, 350);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMissionVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (missionRef.current) observer.observe(missionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (everythingRef.current) observer.observe(everythingRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const animateCounter = (target, index, duration = 1500) => {
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setStatCounters(prev => {
          const next = [...prev];
          next[index] = Math.round(eased * target);
          return next;
        });
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setWhyVisible(true);
          animateCounter(100, 0);
          animateCounter(95, 1);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (whyRef.current) observer.observe(whyRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCustomersVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (customersRef.current) observer.observe(customersRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setContactVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (contactRef.current) observer.observe(contactRef.current);
    return () => observer.disconnect();
  }, []);

  const validateEmail = (value) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      return 'Email is required.';
    }
    if (!emailPattern.test(value)) {
      return 'Please enter a valid email address.';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'email' && emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(formData.email));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check: bots tend to fill hidden fields, humans never see this one
    if (formData._honey) {
      return;
    }

    const emailValidationError = validateEmail(formData.email);
    if (emailValidationError) {
      setEmailTouched(true);
      setEmailError(emailValidationError);
      return;
    }

    setIsSubmitting(true);

    // Using FormSubmit.co - a free form backend service
    try {
      const response = await fetch('https://formsubmit.co/ajax/minthub.business@protonmail.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message
        })
      });

      if (response.ok) {
        setAlertVariant('success');
        setAlertMessage('Thank you! Your message has been sent successfully.');
        setShowAlert(true);
        setFormData({ name: '', email: '', message: '', _honey: '' });
        setEmailTouched(false);
        setEmailError('');
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setAlertVariant('danger');
      setAlertMessage('Sorry, there was an error sending your message. Please try again.');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }

    setTimeout(() => setShowAlert(false), 9000);
  };
  return (
    <div className="App">
      {/* Navigation */}
      <Navbar expand="lg" className="navbar-custom shadow-sm" sticky="top" expanded={navExpanded} onToggle={setNavExpanded}>
        <Container>
          <Navbar.Brand href="#home" className="brand-text d-flex align-items-center">
            <img src="/mintHubLogo.png" alt="MintHub - Professional websites for local businesses" className="brand-logo me-2" />
            <span><span className="brand-mint">Mint</span><span className="brand-hub">Hub</span></span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link href="#home" className="nav-link-custom" onClick={() => setNavExpanded(false)}>Home</Nav.Link>
              <Nav.Link href="#services" className="nav-link-custom" onClick={() => setNavExpanded(false)}>Our Mission</Nav.Link>
              <Nav.Link href="#customers" className="nav-link-custom" onClick={() => setNavExpanded(false)}>Who We Serve</Nav.Link>
              {/*<Nav.Link href="#portfolio" className="nav-link-custom">Portfolio</Nav.Link> */}
              <Nav.Link href="#contact" onClick={() => setNavExpanded(false)}>
                <Button variant="primary" className="btn-custom">Get Started</Button>
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section className="hero-section" id="home">
        <Container>
          <Row className="align-items-center hero-row">
            <Col lg={6} className="text-start">
              <h1 className="hero-title">
                Professional Websites for Local Businesses
              </h1>
              <p className="hero-subtitle">
                We help small and medium businesses establish a powerful online presence
                with stunning, professional websites that drive results.
              </p>
              <div className="hero-buttons">
                {/* <Button size="lg" variant="outline-dark" className="btn-outline-custom">View Our Work</Button> */}
              </div>
            </Col>
            <Col lg={6} className="text-center">
              <div className="hero-image">
                <img src="/mintHubLogo.png" alt="MintHub - Professional web design for local businesses" className="hero-logo" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Mission Section */}
      <section className="services-section mission-section py-5" id="services" ref={missionRef}>
        <Container>
          <h2 className={`section-title text-center mb-4 mission-title${missionVisible ? ' mission-visible' : ''}`}>Our Mission</h2>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Card className={`service-card mission-card shadow-sm border-0${missionVisible ? ' mission-visible' : ''}`}>
                <Card.Body className="p-4 p-md-5">
                  <p className="lead text-center mb-4 mission-line line-1">
                    In today&apos;s world, your online presence is often your first impression. If people can&apos;t find you online, they may never walk through your door.
                  </p>
                  <p className="mb-3 mission-line line-2">
                    At MintHub, our mission is simple: help local businesses stand out, build trust faster, and turn online visitors into real customers.
                  </p>
                  <p className="mb-3 mission-line line-2">
                    Most people don't know what business are around their area. It's our job to make sure they find you, understand what you offer, and choose you over the competition.
                  </p>
                  <p className="mb-3 mission-line line-3">
                    We create websites that make it easy for people to discover your business, understand what you offer, and take action, whether that&apos;s calling, booking, or visiting in person.
                  </p>
                  <p className="mb-0 mission-line line-4">
                    We want more people to find your business, choose your business, and keep coming back.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* We Handle Everything Section */}
      <section className="everything-section py-5" ref={everythingRef}>
        <Container>
          <h2 className="section-title text-center mb-2">We Handle Everything</h2>
          <p className="text-center lead mb-5 everything-subtitle">
            No headaches. No back-and-forth. You focus on your business, let us take care of the rest.
          </p>
          <Row className="justify-content-center g-4 mb-5">
            {[
              { icon: '🎨', title: 'Design', desc: 'Custom, professional visuals built around your brand.' },
              { icon: '💻', title: 'Web Development', desc: 'Fast, modern websites coded and ready to go live.' },
              { icon: '✍️', title: 'Copywriting', desc: 'We write the words — compelling content that converts.' },
              { icon: '🔍', title: 'SEO', desc: 'Optimized from day one so customers can find you online.' },
              { icon: '🌐', title: 'Hosting & Domain', desc: 'We set up and manage your hosting so you never have to.' },
              { icon: '🛠️', title: 'Ongoing Maintenance', desc: 'Updates, fixes, and support — whenever you need it.' },
            ].map((item, i) => (
              <Col key={i} xs={12} sm={6} lg={4}>
                <div
                  className={`everything-card h-100${cardsVisible ? ' visible' : ''}`}
                  style={{ animationDelay: `${i * 0.12}s` }}
                >
                  <div className="everything-icon" role="img" aria-label={item.title}>{item.icon}</div>
                  <div className="everything-card-body">
                    <h5 className="everything-title">{item.title}</h5>
                    <p className="everything-desc">{item.desc}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="text-center">
            <div className="everything-banner">
              <span className="everything-banner-text">
                From the first conversation to a live website — <strong>we've got it covered.</strong>
              </span>
            </div>
          </div>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className={`why-section py-5${whyVisible ? ' why-visible' : ''}`} ref={whyRef}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4">
              <h2 className="section-title">Why Choose MintHub?</h2>
              <p className="lead">
                We understand the unique challenges local businesses face in today's digital landscape.
              </p>
              <ul className="benefits-list">
                <li>✓ Affordable pricing tailored for small businesses and freelancers</li>
                <li>✓ Quick turnaround times without compromising quality</li>
                <li>✓ Ongoing support and maintenance</li>
                <li>✓ Local expertise and personalized service</li>
                <li>✓ Modern, conversion-focused designs</li>
              </ul>
            </Col>
            <Col lg={6} className="text-center">
              {/* Desktop: 2x2 grid */}
              <div className="stats-grid d-none d-md-grid">
                <div className="stat-card">
                  <h3 className="stat-number">{statCounters[0]}+</h3>
                  <p className="stat-label">Happy Clients</p>
                </div>
                <div className="stat-card">
                  <h3 className="stat-number">{statCounters[1]}%</h3>
                  <p className="stat-label">Satisfaction Rate</p>
                </div>
                <div className="stat-card">
                  <h3 className="stat-number">1 Week</h3>
                  <p className="stat-label">Fast Delivery</p>
                </div>
                <div className="stat-card">
                  <h3 className="stat-number">24/7</h3>
                  <p className="stat-label">Support</p>
                </div>
              </div>
              {/* Mobile: Carousel */}
              <Carousel className="stats-carousel d-md-none" indicators={true} controls={false} interval={3000}>
                <Carousel.Item>
                  <div className="stat-card carousel-stat">
                    <h3 className="stat-number">{statCounters[0]}+</h3>
                    <p className="stat-label">Happy Clients</p>
                  </div>
                </Carousel.Item>
                <Carousel.Item>
                  <div className="stat-card carousel-stat">
                    <h3 className="stat-number">{statCounters[1]}%</h3>
                    <p className="stat-label">Satisfaction Rate</p>
                  </div>
                </Carousel.Item>
                <Carousel.Item>
                  <div className="stat-card carousel-stat">
                    <h3 className="stat-number">1 Week</h3>
                    <p className="stat-label">Fast Delivery</p>
                  </div>
                </Carousel.Item>
                <Carousel.Item>
                  <div className="stat-card carousel-stat">
                    <h3 className="stat-number">24/7</h3>
                    <p className="stat-label">Support</p>
                  </div>
                </Carousel.Item>
              </Carousel>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Who We Serve Section */}
      <section className={`customers-section py-5${customersVisible ? ' customers-visible' : ''}`} id="customers" ref={customersRef}>
        <Container>
          <h2 className="section-title text-center mb-3">Who We Serve</h2>
          <p className="text-center mb-5 lead">
            We build tailored web solutions for businesses just like yours.
          </p>
          <div className="customers-flex">
            {[
              { icon: '🏪', title: 'Small Businesses', text: 'Websites designed for teams of 1–20 that punch above their weight — professional, fast, and built to grow with you.' },
              { icon: '🔧', title: 'Local Service Providers', text: 'Plumbers, electricians, landscapers — get found by local customers with a site that showcases your work and drives calls.' },
              { icon: '🍽️', title: 'Restaurants & Cafes', text: 'Beautiful menus, online ordering integration, and reservation-ready pages that bring hungry customers through your door.' },
              { icon: '🛍️', title: 'Retail Shops', text: 'Showcase your products with eye-catching storefronts, e-commerce features, and location-based SEO to drive foot traffic.' },
              { icon: '⚖️', title: 'Professional Services', text: 'Lawyers, accountants, consultants — establish authority online with polished sites that convert visitors into clients.' },
              { icon: '🏠', title: 'Home Service Businesses', text: 'Cleaning, HVAC, pest control and more — build trust with before/after galleries, reviews, and easy quote-request forms.' },
            ].map((item, index) => (
              <Card key={index} className={`customer-card ${openCard === index ? 'card-open' : ''}`}>
                <div className="customer-card-header" onClick={() => toggleCard(index)}>
                  <div className="customer-icon" role="img" aria-label={item.title}>{item.icon}</div>
                  <Card.Title className="mb-0">{item.title}</Card.Title>
                  <span className="card-chevron" aria-hidden="true">▾</span>
                </div>
                <Card.Body>
                  <div className="customer-icon desktop-only" aria-hidden="true">{item.icon}</div>
                  <Card.Title className="desktop-only">{item.title}</Card.Title>
                  <Card.Text>{item.text}</Card.Text>
                </Card.Body>
              </Card>
            ))}
          </div>
          <div className="text-center mt-4">
            <p className="customers-cta">
              Don't see your industry? We work with <strong>ALL local businesses</strong>. <a href="#contact">Let's talk</a> about your needs.
            </p>
          </div>
        </Container>
      </section>

      {/* Contact Section */}
      <section className={`contact-section py-5${contactVisible ? ' contact-visible' : ''}`} id="contact" ref={contactRef}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <h2 className="section-title text-center mb-4">Get In Touch</h2>
              <p className="text-center mb-5 lead">
                Ready to start your web journey? Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {showAlert && (
                <Alert variant={alertVariant} onClose={() => setShowAlert(false)} dismissible>
                  {alertMessage}
                </Alert>
              )}

              <Form onSubmit={handleSubmit} className="contact-form">
                <Form.Group className="mb-4">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="form-input"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    placeholder="your.email@example.com"
                    required
                    className="form-input"
                    isInvalid={emailTouched && !!emailError}
                  />
                  <Form.Control.Feedback type="invalid">
                    {emailError}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Tell us about your vision..."
                    required
                    className="form-input"
                  />
                </Form.Group>

                {/* Honeypot field - hidden from real users, catches bots */}
                <Form.Group className="honeypot-field" aria-hidden="true">
                  <Form.Control
                    type="text"
                    name="_honey"
                    value={formData._honey}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </Form.Group>

                <div className="text-center">
                  <Button type="submit" size="lg" className="btn-custom px-5" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
